if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slider = this.querySelector('.announcement__slider');
      this.slides = [...this.querySelectorAll('.announcement__text')];
      this.index = 0;
      this.userPaused = this.dataset.autoplay === 'false' || Boolean(window.Shopify?.designMode);
      this.pointerInside = false;
      this.focusInside = false;
      this.inViewport = true;
      this.desktop = window.matchMedia('(min-width: 769px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.localization = this.querySelector('.announcement__localization');
      this.localizationForm = this.localization?.querySelector('form');
      this.links = [...this.querySelectorAll('.js-announcement-link')];
      this.toggle = this.querySelector('[data-announcement-toggle]');
      this.counter = this.querySelector('[data-announcement-index]');

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.manualSlide(-1), { signal });
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.manualSlide(1), { signal });
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.pointerInside = true;
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.pointerInside = false;
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focusInside = true;
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        this.focusInside = this.contains(event.relatedTarget);
        this.syncAutoplay();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const selected = event.target.closest('.announcement__text');
        const index = this.slides.indexOf(selected);
        if (index < 0) return;
        this.userPaused = true;
        this.showSlide(index, true);
        this.syncAutoplay();
      }, { signal });
      this.desktop.addEventListener('change', () => this.moveMobileContent(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncAutoplay(), { signal });
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.syncAutoplay();
      });
      this.observer.observe(this);
      this.showSlide(0);
      this.moveMobileContent();
      this.syncAutoplay();

      // The shared select measures hidden options on initialisation, which
      // can produce -32px. This bar sizes its selectors naturally instead.
      customElements.whenDefined('custom-select').then(() => {
        if (this.isConnected) this.clearMeasuredWidths();
      });
    }

    showSlide(index, announce = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slider.setAttribute('aria-live', announce ? 'polite' : 'off');
      this.slides.forEach((slide, i) => {
        const current = i === this.index;
        slide.classList.toggle('is-visible', current);
        slide.setAttribute('aria-hidden', String(!current));
        slide.inert = !current;
      });
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    }

    manualSlide(direction) {
      this.userPaused = true;
      this.showSlide(this.index + direction, true);
      this.syncAutoplay();
    }

    syncAutoplay() {
      clearTimeout(this.timer);
      this.timer = null;
      const paused = this.userPaused || this.reducedMotion.matches;
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
        this.toggle.querySelector('[data-play-icon]').hidden = !paused;
        this.toggle.querySelector('[data-pause-icon]').hidden = paused;
        this.toggle.disabled = this.reducedMotion.matches;
      }
      if (paused || this.pointerInside || this.focusInside || !this.inViewport || document.hidden || this.slides.length < 2) return;
      const delay = Math.max(3000, Number(this.dataset.slideDelay) || 9000);
      this.timer = setTimeout(() => {
        this.showSlide(this.index + 1);
        this.syncAutoplay();
      }, delay);
    }

    clearMeasuredWidths() {
      this.localizationForm?.querySelectorAll('.custom-select__btn').forEach(button => {
        button.style.removeProperty('--custom-select-button-width');
      });
    }

    moveMobileContent() {
      const menu = document.querySelector('.main-menu__content');
      if (!menu) return;
      if (!this.desktop.matches) {
        if (this.localizationForm && !this.mobileLocalization) {
          this.mobileLocalization = document.createElement('div');
          this.mobileLocalization.className = 'mob__localization';
          this.mobileLocalization.appendChild(this.localizationForm);
          menu.appendChild(this.mobileLocalization);
        }
        if (this.links.length && !this.mobileLinks) {
          this.mobileLinks = document.createElement('nav');
          this.mobileLinks.className = 'mob__announcement-links secondary-nav';
          const list = document.createElement('ul');
          this.links.forEach(link => {
            const item = document.createElement('li');
            const mobileLink = link.cloneNode(true);
            mobileLink.className = 'secondary-nav__item';
            item.appendChild(mobileLink);
            list.appendChild(item);
          });
          this.mobileLinks.appendChild(list);
          menu.appendChild(this.mobileLinks);
        }
      } else {
        if (this.mobileLocalization) {
          this.localization.appendChild(this.localizationForm);
          this.mobileLocalization.remove();
          this.mobileLocalization = null;
        }
        this.mobileLinks?.remove();
        this.mobileLinks = null;
      }
      this.clearMeasuredWidths();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      if (this.mobileLocalization) {
        this.localization.appendChild(this.localizationForm);
        this.mobileLocalization.remove();
        this.mobileLocalization = null;
      }
      this.mobileLinks?.remove();
      this.mobileLinks = null;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
